/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown, Star } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { INTENT_CLS, fmtDur, fmtTime, RecordingPlayer, Stars } from "./AgentCallsTable";
import { RateCallDialog } from "./RateCallDialog";

const scoreCls = (s) => (s >= 75 ? "bg-emerald-100 text-emerald-800" : s >= 50 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700");

/* 通话质量评价清单：AI 话术评分 + 经理人工评分 */
export function AgentQualityList({ items: scoredItems, allItems = [], zh, canRate, onChanged, autoRateId, onAutoRateDone }) {
  const { api } = useApp();
  const navigate = useNavigate();
  const [sort, setSort] = useState("time");
  const [rating, setRating] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);
  useEffect(() => {
    if (!autoRateId || !canRate) return;
    const c = allItems.find((x) => x.id === autoRateId);
    if (!c) return;
    if (!scoredItems.some((x) => x.id === c.id)) setIncludeAll(true);
    setRating(c); onAutoRateDone?.();
  }, [autoRateId, allItems]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = includeAll ? allItems : scoredItems;
  const sorted = useMemo(() => {
    const arr = [...items];
    if (sort === "score_desc") arr.sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
    else if (sort === "score_asc") arr.sort((a, b) => (a.score ?? 101) - (b.score ?? 101));
    else if (sort === "stars") arr.sort((a, b) => (b.managerRating?.stars || 0) - (a.managerRating?.stars || 0));
    return arr;
  }, [items, sort]);
  const dist = useMemo(() => {
    const d = { good: 0, mid: 0, low: 0 };
    items.forEach((c) => { if (typeof c.score === "number") d[c.score >= 75 ? "good" : c.score >= 50 ? "mid" : "low"] += 1; });
    return d;
  }, [items]);
  const scoredN = dist.good + dist.mid + dist.low;
  return (
    <div data-testid="agent-quality-list" data-count={items.length}>
      <div className="flex items-center gap-3 px-3 pt-3 flex-wrap">
        {scoredN > 0 && (
          <div className="flex-wrap md:flex-nowrap flex items-center gap-2 text-[11px]" data-testid="agent-quality-dist">
            <span className="text-slate-400">{zh ? "AI 评分分布" : "AI score"}</span>
            <div className="h-2 w-40 rounded-full overflow-hidden flex bg-slate-100">
              <div className="bg-emerald-500" style={{ width: `${(dist.good / scoredN) * 100}%` }} />
              <div className="bg-amber-400" style={{ width: `${(dist.mid / scoredN) * 100}%` }} />
              <div className="bg-red-400" style={{ width: `${(dist.low / scoredN) * 100}%` }} />
            </div>
            <span className="text-emerald-700">{zh ? "优" : "good"} {Math.round((dist.good / scoredN) * 100)}%</span>
            <span className="text-amber-700">{zh ? "中" : "mid"} {Math.round((dist.mid / scoredN) * 100)}%</span>
            <span className="text-red-600">{zh ? "待改进" : "low"} {Math.round((dist.low / scoredN) * 100)}%</span>
          </div>
        )}
        <div className="ml-auto inline-flex items-center gap-2 text-xs">
          {canRate && (
            <label className="inline-flex items-center gap-1 text-slate-500 cursor-pointer" data-testid="agent-quality-include-all">
              <input type="checkbox" checked={includeAll} onChange={(e) => setIncludeAll(e.target.checked)} />{zh ? "含未评分通话" : "Include unscored"}
            </label>
          )}
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" />
          <select data-testid="agent-quality-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-slate-200 rounded-md px-2 py-1 text-xs">
            <option value="time">{zh ? "按时间" : "By time"}</option>
            <option value="score_desc">{zh ? "AI 评分 高→低" : "AI score ↓"}</option>
            <option value="score_asc">{zh ? "AI 评分 低→高" : "AI score ↑"}</option>
            <option value="stars">{zh ? "经理评分 高→低" : "Manager stars ↓"}</option>
          </select>
        </div>
      </div>
      {sorted.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-10" data-testid="agent-quality-empty">{zh ? "该时间范围内暂无已评分通话（通话录音转写后由 AI 自动评分）" : "No scored calls in this range"}</p>
      ) : (
        <div className="divide-y divide-slate-50 mt-2">
          {sorted.map((c) => (
            <div key={c.id} data-testid={`agent-quality-${c.id}`} className="px-3 py-3 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-start">
              <div className="flex md:flex-col items-center gap-2 md:w-16">
                {typeof c.score === "number" ? <span className={`text-base font-extrabold rounded-lg px-2.5 py-1 ${scoreCls(c.score)}`} data-testid={`agent-quality-score-${c.id}`}>{c.score}</span> : <span className="text-xs text-slate-300">AI —</span>}
                {c.managerRating?.stars ? <Stars n={c.managerRating.stars} testid={`agent-quality-stars-${c.id}`} /> : <span className="text-[10px] text-slate-300">{zh ? "未人评" : "no stars"}</span>}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  {c.leadId ? <button onClick={() => navigate(`/crm/leads/${c.leadId}`)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 font-semibold text-amber-700 hover:underline">{c.name || c.customerNumber}</button> : <span className="font-semibold text-slate-800">{c.name || c.customerNumber}</span>}
                  <span className="font-mono text-slate-400">{c.customerNumber}</span>
                  <span className="text-slate-400">{fmtTime(c.createdAt)} · {fmtDur(c.durationSec)}</span>
                  {c.intentTag && <span className={`rounded-full px-2 py-0.5 ${INTENT_CLS[c.intentTag] || "bg-slate-100 text-slate-500"}`}>{c.intentTag}</span>}
                </div>
                {c.summary && <p className="text-sm text-slate-700 mt-1">{c.summary}</p>}
                {c.highlight && <p className="text-xs text-emerald-700 mt-1">✦ {zh ? "亮点：" : "Highlight: "}{c.highlight}</p>}
                {(c.tips || []).length > 0 && <ul className="mt-1 text-xs text-slate-500 list-disc pl-4 space-y-0.5">{c.tips.slice(0, 3).map((t, i) => <li key={i}>{t}</li>)}</ul>}
                {c.managerRating?.comment && <p className="text-xs text-slate-600 mt-1.5 bg-amber-50 border border-amber-100 rounded px-2 py-1" data-testid={`agent-quality-comment-${c.id}`}>💬 {c.managerRating.byName || c.managerRating.by}：{c.managerRating.comment}</p>}
              </div>
              <div className="flex flex-col items-end gap-2">
                {c.hasRecording && <RecordingPlayer id={c.id} testid={`agent-quality-rec-${c.id}`} />}
                {canRate && (
                  <button data-testid={`agent-quality-rate-${c.id}`} onClick={() => setRating(c)}
                    className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-xs border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-lg px-2.5 py-1.5">
                    <Star className="h-3.5 w-3.5" />{c.managerRating?.stars ? (zh ? "修改评分" : "Edit rating") : (zh ? "经理评分" : "Rate")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {rating && <RateCallDialog api={api} call={rating} zh={zh} onClose={() => setRating(null)} onSaved={onChanged} />}
    </div>
  );
}
